import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { getOr, isEmpty } from 'lodash/fp';

import api from './api';

const name = 'businesses';

const fetchMoreQty = 10;
const limit = 19;

// TODO move axios to API folder
const fetchBusinesses = createAsyncThunk(
  'businesses/fetchAllBusinesses',
  async (defaultType) => {
    const fn = api.fetchBusinesses(limit, defaultType);
    const response = await fn();

    return response;
  }
);

const updateBusiness = createAsyncThunk(
  'businesses/updateBusiness',
  async (payload) => {
    return payload;
  }
);

const fetchBusiness = createAsyncThunk(
  'businesses/fetchBusiness',
  async (businessId) => {
    const fn = api.fetchBusiness(businessId);
    const response = await fn();

    return response;
  }
);

const checkGiftCardConfigBalance = createAsyncThunk(
  'businesses/checkGiftCardConfigBalance',
  async (id) => {
    const fn = api.fetchBusiness(id, true);
    const response = await fn();

    return response;
  }
);

const fetchMerchant = createAsyncThunk(
  'businesses/fetchMerchant',
  async (businessId) => {
    const fn = api.fetchMerchant(businessId);
    const response = await fn();

    return response;
  }
);

const fetchMore = createAsyncThunk(
  'businesses/fetchMore',
  async (
    { lastBusinessId, selectedType: typeId, selectedCategory: categoryId },
    { getState }
  ) => {
    const { order } = getState().businesses;
    const fn = api.fetchMore(
      fetchMoreQty,
      typeId,
      categoryId,
      lastBusinessId,
      order
    );
    const response = await fn();

    return response;
  }
);

const searchByName = createAsyncThunk(
  'businesses/searchByName',
  async ({ search: businessName }) => {
    const fn = api.searchByName(businessName);
    const response = await fn();

    return response;
  }
);

const filterByCategory = createAsyncThunk(
  'businesses/filterByCategory',
  async ({ typeId, categoryId }) => {
    const fn = api.filterByCategory(limit, typeId, categoryId);
    const response = await fn();

    return response;
  }
);

const filterByType = createAsyncThunk(
  'businesses/filterByType',
  async (typeId) => {
    const fn = api.filterByType(limit, typeId);
    const response = await fn();

    return response;
  }
);
const orderBy = createAsyncThunk('businesses/orderBy', async (payload) => {
  const { typeId, categoryId, order } = payload;
  const fn = api.orderBy(limit, typeId, categoryId, order);
  const response = await fn();
  return response;
});

const preloadData = [{ id: '1' }];

const { actions, reducer } = {
  ...createSlice({
    name,
    initialState: {
      isLoading: false,
      isLoadingMore: false,
      isAll: false,
      error: false,
      data: [...preloadData],
      order: '',
      business: {},
    },
    extraReducers: (builder) => {
      const setLoading = (state) => {
        state.isLoading = true;
        state.data = [...preloadData];
        state.error = {};
        state.order = '';
      };

      const setMoreLoading = (state) => {
        state.isLoadingMore = true;
        state.error = {};
      };

      const setError = (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      };

      const setMoreError = (state, action) => {
        state.isLoadingMore = false;
        state.error = action.payload;
      };

      const getDisplayData = (action, count) =>
        getOr([], 'payload', action).slice(0, count - 1);
      const getLast = (action, count) =>
        getOr([], 'payload', action)[count - 1];

      builder
        // Fetch all businesses
        .addCase(fetchBusinesses.pending, setLoading)
        .addCase(fetchBusinesses.fulfilled, (state, action) => {
          state.isLoading = false;
          const last = getLast(action, limit);
          state.data = getDisplayData(action, limit);
          state.isAll = !isEmpty(last);
        })
        .addCase(fetchBusinesses.rejected, setError)

        // Fetch more
        .addCase(fetchMore.pending, setMoreLoading)
        .addCase(fetchMore.fulfilled, (state, action) => {
          state.isLoadingMore = false;
          const last = getLast(action, fetchMoreQty);
          state.data = [...state.data, ...getDisplayData(action, fetchMoreQty)];
          state.isAll = !isEmpty(last);
        })
        .addCase(fetchMore.rejected, setMoreError)

        // Search by name
        .addCase(searchByName.pending, (state) => {
          state.isLoading = true;
          state.data = [...preloadData];
          state.order = '';
        })
        .addCase(searchByName.fulfilled, (state, action) => {
          state.isLoading = false;
          state.data = getOr([], 'payload', action);
          state.isAll = false;
        })
        .addCase(searchByName.rejected, setError)

        // Filter by category
        .addCase(filterByCategory.pending, setLoading)
        .addCase(filterByCategory.fulfilled, (state, action) => {
          state.isLoading = false;
          const last = getLast(action, limit);
          state.data = getDisplayData(action, limit);
          state.isAll = !isEmpty(last);
        })
        .addCase(filterByCategory.rejected, setError)

        // Filter by type
        .addCase(filterByType.pending, setLoading)
        .addCase(filterByType.fulfilled, (state, action) => {
          state.isLoading = false;
          const last = getLast(action, limit);
          state.data = getDisplayData(action, limit);
          state.isAll = !isEmpty(last);
        })
        .addCase(filterByType.rejected, setError)

        // Fetch business
        .addCase(fetchBusiness.pending, (state) => {
          state.isLoading = true;
          state.data = [...preloadData];
        })
        .addCase(fetchBusiness.fulfilled, (state, action) => {
          state.isLoading = false;
          state.business = getOr([], 'payload', action);
        })
        .addCase(fetchBusiness.rejected, setError)

        // Check gift card balance
        .addCase(checkGiftCardConfigBalance.pending, (state) => {
          state.isLoading = true;
          state.data = [...preloadData];
        })
        .addCase(checkGiftCardConfigBalance.fulfilled, (state, action) => {
          state.isLoading = false;
          state.business = getOr([], 'payload', action);
        })

        // Update business
        .addCase(updateBusiness.fulfilled, (state, action) => {
          state.isLoading = false;
          state.business = {
            ...state.business,
            ...getOr({}, 'payload', action),
          };
        })

        // Fetch merchant
        .addCase(fetchMerchant.pending, (state) => {
          state.isLoading = true;
          state.data = [...preloadData];
        })
        .addCase(fetchMerchant.fulfilled, (state, action) => {
          state.isLoading = false;
          state.business = getOr([], 'payload', action);
        })
        .addCase(fetchMerchant.rejected, setError)

        // Order by
        .addCase(orderBy.pending, (state) => {
          state.isLoading = true;
          state.data = [...preloadData];
        })
        .addCase(orderBy.fulfilled, (state, action) => {
          state.isLoading = false;
          const last = getLast(action, limit);
          state.data = getDisplayData(action, limit);
          state.order = getOr('', ['meta', 'arg', 'order'], action);
          state.isAll = !isEmpty(last);
        })
        .addCase(orderBy.rejected, (state, action) => {
          state.isLoading = false;
          state.order = '';
          state.error = action.payload;
        });
    },
  }),
};

const selectors = {
  // Businesses
  selectAllBusinesses: (state) => getOr([], 'data', state[name]),
  selectBusiness: (state) => getOr({}, 'business', state[name]),
  selectIsLoading: (state) => getOr(false, 'isLoading', state[name]),
  selectLastBusinessId: (state) =>
    getOr('', 'id', state[name].data[state[name].data.length - 1]),
  selectIsAll: (state) => getOr([], 'isAll', state[name]),
  selectisLoadingMore: (state) => getOr([], 'isLoadingMore', state[name]),
};

export default {
  actions: {
    ...actions,
    fetchBusinesses,
    updateBusiness,
    fetchMore,
    searchByName,
    filterByType,
    filterByCategory,
    fetchBusiness,
    checkGiftCardConfigBalance,
    fetchMerchant,
    orderBy,
  },
  selectors,
  reducer,
  name,
};
